interface StorageMessage {
  action: 'get' | 'set' | 'remove' | 'clear';
  key?: string;
  value?: string;
}

interface StorageProps {
  secure?: boolean;
}

export const STORAGE_MESSAGE_TYPE = 'secureStorage';

/**
 * This storage is will force to keep tokens on mobile side or will work as usual 
 * localstorage where no webview detected 
 */
class SecureStorage {
  private static messageId = 0;

  private static pendingPromises: Map<number, any> = new Map();

  private static secureItems: Map<string, string> = new Map();

  private static isReactNativeWebView(): boolean {
    return Boolean((window as any)?.ReactNativeWebView);
  }

  private static generateMessageId(): number {
    return ++this.messageId;
  }

  private static sendMessageToReactNative(message: StorageMessage, canBeRejected = false): Promise<any> {
    return new Promise((resolve, reject) => {
      const messageId = this.generateMessageId();
      this.pendingPromises.set(messageId, {
        resolve,
        reject,
      });

      const messageWithId = {
        ...message,
        type: STORAGE_MESSAGE_TYPE,
        messageId,
      };

      (window as any).ReactNativeWebView.postMessage(JSON.stringify(messageWithId));

      if (canBeRejected) {
        setTimeout(() => {
          if (this.pendingPromises.has(messageId)) {
            this.pendingPromises.delete(messageId);
            reject(new Error('LocalStorage operation timed out'));
          }
        }, 2_000);
      }
    });
  }

  /* Handle response from React Native */
  public static handleReactNativeResponse(messageId: number, value: any): void {
    const promise = this.pendingPromises.get(messageId);

    if (promise) {
      this.pendingPromises.delete(messageId);
      promise.resolve(value);
    }
  }

  public static async getItem(key: string): Promise<string | null> {
    if (this.secureItems.has(key)) {
      return this.secureItems.get(key) || null;
    }

    if (this.isReactNativeWebView()) {
      try {
        const value = await this.sendMessageToReactNative({ action: 'get',
          key }, true);
        this.secureItems.set(key, value);

        return value;
      } catch (error) {
        return this.secureItems.get(key) || null;
      }
    }

    return window.localStorage.getItem(key);
  }

  public static getItemSync(key: string): string | null {
    if (this.secureItems.has(key)) {
      return this.secureItems.get(key) || null;
    }

    return window.localStorage.getItem(key) || null;
  }

  public static async setItem(key: string, value: string, props: StorageProps = {}) {
    if (this.isReactNativeWebView() && props.secure) {
      this.secureItems.set(key, value);

      try {
        await this.sendMessageToReactNative({
          action: 'set',
          key,
          value, 
        });
      } catch (error) {
        console.error(error);
      }
    }

    window.localStorage.setItem(key, value);
  }

  public static async removeItem(key: string) {
    if (this.isReactNativeWebView()) {
      return this.sendMessageToReactNative({ action: 'remove',
        key });
    }

    window.localStorage.removeItem(key);
  }

  public static async clear() {
    window.localStorage.clear();

    if (this.isReactNativeWebView()) {
      return this.sendMessageToReactNative({ action: 'clear' }).then();
    }
  }
}

export default SecureStorage;
